/**
 * DFH Shopify Theme — theme.js
 * Single vanilla JS bundle. No frameworks, no jQuery.
 * Modules: header, mobile menu, cart drawer, variant selection,
 *   qty stepper, sticky ATC, compare bar, FAB shift, tabs, FAQ accordion,
 *   filter accordion, delivery accordion, reveal-on-scroll, WhatsApp pre-fill.
 */

(function () {
  'use strict';

  /* ============================================================
     UTILS
  ============================================================ */
  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return [...(ctx || document).querySelectorAll(sel)]; }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts); }

  /* ============================================================
     HEADER — scroll shadow
  ============================================================ */
  function initHeader() {
    var header = qs('#site-header');
    if (!header) return;
    var scrolled = false;
    window.addEventListener('scroll', function () {
      var s = window.scrollY > 10;
      if (s !== scrolled) {
        scrolled = s;
        header.classList.toggle('has-shadow', s);
      }
    }, { passive: true });
  }

  /* ============================================================
     SEARCH MODAL
  ============================================================ */
  function initSearch() {
    var modal = qs('#search-modal');
    var input = qs('#search-input');
    if (!modal) return;

    function openSearch() {
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (input) setTimeout(function () { input.focus(); }, 50);
    }
    function closeSearch() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    qsa('[data-search-toggle]').forEach(function (btn) {
      on(btn, 'click', openSearch);
    });
    qsa('[data-search-close]').forEach(function (el) {
      on(el, 'click', closeSearch);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeSearch();
    });
  }

  /* ============================================================
     MOBILE MENU
  ============================================================ */
  function initMobileMenu() {
    var menu = qs('#mobile-nav');
    var openBtns = qsa('[data-mobile-menu-toggle]');
    var closeBtns = qsa('[data-mobile-menu-close]');
    if (!menu) return;

    function open() {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      openBtns.forEach(function (btn) { btn.setAttribute('aria-expanded', 'true'); });
      document.body.style.overflow = 'hidden';
      var firstLink = qs('a, button', menu);
      if (firstLink) firstLink.focus();
    }
    function close() {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      openBtns.forEach(function (btn) { btn.setAttribute('aria-expanded', 'false'); });
      document.body.style.overflow = '';
    }

    openBtns.forEach(function (btn) { on(btn, 'click', open); });
    closeBtns.forEach(function (btn) { on(btn, 'click', close); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });
  }

  /* ============================================================
     CART DRAWER
  ============================================================ */
  function initCartDrawer() {
    var drawer = qs('#cart-drawer');
    var overlay = qs('.cart-drawer-overlay');
    if (!drawer) return;

    function openDrawer() {
      drawer.classList.add('is-open');
      overlay && overlay.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      shiftFAB();
    }
    function closeDrawer() {
      drawer.classList.remove('is-open');
      overlay && overlay.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      shiftFAB();
    }

    // Open via header cart button
    qsa('[data-cart-toggle]').forEach(function (btn) {
      on(btn, 'click', openDrawer);
    });
    // Close via overlay or close btn
    qsa('[data-cart-close]').forEach(function (el) {
      on(el, 'click', closeDrawer);
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });

    // Intercept Add-to-Cart form submit
    on(document, 'submit', function (e) {
      var form = e.target.closest('[data-product-form]');
      if (!form) return;
      e.preventDefault();
      var btn = qs('[data-atc-btn]', form);
      if (btn) btn.disabled = true;

      var fd = new FormData(form);
      fetch('/cart/add.js', { method: 'POST', body: fd })
        .then(function (r) { return r.json(); })
        .then(function () {
          return fetch('/cart.js').then(function (r) { return r.json(); });
        })
        .then(function (cart) {
          renderCartDrawer(cart);
          openDrawer();
        })
        .catch(function (err) { console.error('ATC error', err); })
        .finally(function () { if (btn) btn.disabled = false; });
    });

    // Sticky ATC button
    on(document, 'click', function (e) {
      var stickyBtn = e.target.closest('[data-sticky-atc]');
      if (!stickyBtn) return;
      var variantId = stickyBtn.dataset.stickyAtc;
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
        .then(function (cart) { renderCartDrawer(cart); openDrawer(); })
        .catch(function (err) { console.error('Sticky ATC error', err); });
    });

    // Remove item from drawer
    on(qs('.cart-drawer-body'), 'click', function (e) {
      var removeBtn = e.target.closest('[data-remove]');
      if (!removeBtn) return;
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: removeBtn.dataset.remove, quantity: 0 })
      })
        .then(function (r) { return r.json(); })
        .then(renderCartDrawer);
    });

    // Qty changes in drawer
    on(qs('.cart-drawer-body'), 'click', function (e) {
      var btn = e.target.closest('[data-qty-minus],[data-qty-plus]');
      if (!btn) return;
      var key = btn.dataset.qtyMinus || btn.dataset.qtyPlus;
      var delta = btn.hasAttribute('data-qty-minus') ? -1 : 1;
      var item = btn.closest('.cart-drawer-item');
      var qtyEl = qs('span', item);
      var newQty = Math.max(0, parseInt(qtyEl.textContent) + delta);

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: newQty })
      })
        .then(function (r) { return r.json(); })
        .then(renderCartDrawer);
    });
  }

  function renderCartDrawer(cart) {
    // Update count badges — header + bottom nav
    qsa('[data-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
    qsa('.cart-count').forEach(function (el) {
      el.textContent = cart.item_count;
      el.style.display = cart.item_count > 0 ? '' : 'none';
    });
    var mbnBadge = qs('.mbn-badge');
    if (mbnBadge) {
      mbnBadge.textContent = cart.item_count;
      mbnBadge.style.display = cart.item_count > 0 ? '' : 'none';
    }
    // Update total
    qsa('[data-cart-total]').forEach(function (el) {
      el.textContent = 'KES ' + (cart.total_price / 100).toLocaleString('en-KE');
    });
    // Re-render items
    var body = qs('[data-cart-body]');
    if (!body) return;
    if (cart.item_count === 0) {
      body.innerHTML = '<div class="cart-empty"><p>Your cart is empty</p></div>';
      return;
    }
    body.innerHTML = cart.items.map(function (item) {
      return '<div class="cart-drawer-item" data-cart-item="' + item.key + '">' +
        '<div class="cart-item-img">' +
          (item.image ? '<img src="' + item.image + '" alt="" width="64" height="64" loading="lazy">' : '') +
        '</div>' +
        '<div>' +
          '<div class="cart-item-name">' + item.product_title + '</div>' +
          (item.variant_title && item.variant_title !== 'Default Title'
            ? '<div class="cart-item-variant">' + item.variant_title + '</div>'
            : '') +
          '<div class="cart-item-qty">' +
            '<button data-qty-minus="' + item.key + '" aria-label="Decrease">&#8722;</button>' +
            '<span>' + item.quantity + '</span>' +
            '<button data-qty-plus="' + item.key + '" aria-label="Increase">&#43;</button>' +
          '</div>' +
        '</div>' +
        '<div class="cart-item-right">' +
          '<span class="cart-item-price">KES ' + (item.final_line_price / 100).toLocaleString('en-KE') + '</span>' +
          '<button class="cart-item-remove" data-remove="' + item.key + '" aria-label="Remove ' + item.product_title + ' from cart">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
          '</button>' +
        '</div>' +
      '</div>';
    }).join('');
  }

  /* ============================================================
     QUANTITY STEPPER
  ============================================================ */
  function initQtyStepper() {
    on(document, 'click', function (e) {
      var btn = e.target.closest('[data-qty-step]');
      if (!btn) return;
      var step = parseInt(btn.dataset.qtyStep);
      var input = qs('input[type="number"]', btn.closest('.qty-stepper'));
      if (!input) return;
      var val = Math.max(1, parseInt(input.value) + step);
      input.value = val;
    });
  }

  /* ============================================================
     VARIANT SELECTION
  ============================================================ */
  function initVariants() {
    var infoPanel = qs('[data-product-info]');
    if (!infoPanel) return;

    var variantsJson = qs('#product-variants-json');
    if (!variantsJson) return;
    var variants = JSON.parse(variantsJson.textContent);

    var selectedOptions = [];

    // Seed from currently active chips/swatches
    qsa('.var-group', infoPanel).forEach(function (group, groupIdx) {
      var active = qs('.active[data-option-value]', group);
      selectedOptions[groupIdx] = active ? active.dataset.optionValue : null;
    });

    function findVariant() {
      return variants.find(function (v) {
        return selectedOptions.every(function (opt, i) {
          return opt == null || v.options[i] === opt;
        });
      }) || variants[0];
    }

    function updateUI(variant) {
      if (!variant) return;
      // Update hidden variant id
      var idInput = qs('#variant-id');
      if (idInput) idInput.value = variant.id;

      // Update price display
      var priceEl = qs('[data-product-price]');
      if (priceEl) priceEl.textContent = 'KES ' + (variant.price / 100).toLocaleString('en-KE');

      // Update ATC button price
      var atcPrice = qs('[data-atc-price]');
      if (atcPrice) atcPrice.textContent = (variant.price / 100).toLocaleString('en-KE');

      // Update ATC button id
      var atcBtn = qs('[data-sticky-atc]');
      if (atcBtn) atcBtn.dataset.stickyAtc = variant.id;

      // Update sticky price
      var stickyPrice = qs('[data-sticky-price]');
      if (stickyPrice) stickyPrice.textContent = (variant.price / 100).toLocaleString('en-KE');

      // Update WhatsApp href
      updateWALink(variant);

      // Unavailable chip states
      qsa('[data-option-value]', infoPanel).forEach(function (btn) {
        btn.classList.remove('disabled');
      });
      variants.forEach(function (v) {
        if (!v.available) {
          var idx = v.options.findIndex(function (_, i) { return i === 0; });
          var btn = qs('[data-option-value="' + v.options[0] + '"]', infoPanel);
          if (btn) btn.classList.add('disabled');
        }
      });
    }

    function updateWALink(variant) {
      var waBtn = qs('[data-wa-btn]');
      if (!waBtn) return;
      var title = encodeURIComponent(
        qs('.pdp-title') ? qs('.pdp-title').textContent.trim() : ''
      );
      var productUrl = encodeURIComponent(window.location.href);
      var variantTitle = encodeURIComponent(variant.title || '');
      var price = (variant.price / 100).toLocaleString('en-KE');
      var msg = encodeURIComponent(
        'Hi DFH, I\'m interested in ' + decodeURIComponent(title) +
        ' (' + decodeURIComponent(productUrl) + '). ' +
        'Variant: ' + decodeURIComponent(variantTitle) + '. ' +
        'Price: KES ' + price + '.'
      );
      var waNumber = waBtn.href.match(/wa\.me\/(\d+)/);
      if (waNumber) {
        waBtn.href = 'https://wa.me/' + waNumber[1] + '?text=' + msg;
      }
      // Sticky WA button
      var stickyWA = qs('#sticky-wa-btn');
      if (stickyWA && waNumber) {
        stickyWA.href = 'https://wa.me/' + waNumber[1] + '?text=' + msg;
      }
    }

    // Listen for chip / swatch clicks
    on(infoPanel, 'click', function (e) {
      var btn = e.target.closest('[data-option-value]');
      if (!btn || btn.classList.contains('disabled')) return;

      var groupIdx = parseInt(btn.dataset.optionIndex);
      selectedOptions[groupIdx] = btn.dataset.optionValue;

      // Update active state in this group
      var group = btn.closest('.var-group');
      qsa('[data-option-value]', group).forEach(function (b) {
        b.classList.remove('active');
        b.setAttribute('aria-pressed', 'false');
      });
      btn.classList.add('active');
      btn.setAttribute('aria-pressed', 'true');

      // Update label
      var label = qs('[data-selected-option="' + groupIdx + '"]', infoPanel);
      if (label) label.textContent = btn.dataset.optionValue;

      updateUI(findVariant());
    });

    // Initial WA link build
    updateWALink(findVariant());
  }

  /* ============================================================
     STICKY ATC OBSERVER
  ============================================================ */
  function initStickyATC() {
    var atcRow = qs('.atc-row');
    var stickyBar = qs('#sticky-atc');
    if (!atcRow || !stickyBar) return;

    var io = new IntersectionObserver(function (entries) {
      var visible = entries[0].isIntersecting;
      stickyBar.classList.toggle('show', !visible);
      stickyBar.setAttribute('aria-hidden', String(visible));
      shiftFAB();
    }, { threshold: 0 });
    io.observe(atcRow);
  }

  /* ============================================================
     HERO CAROUSEL
  ============================================================ */
  function initHeroCarousel() {
    var root = qs('[data-hero-carousel]');
    if (!root) return;
    var track = qs('.hc-track', root);
    var slides = qsa('.hc-slide', root);
    if (!track || slides.length < 2) return; // single slide — nothing to wire up

    var tabs = qsa('[data-hc-tab]', root);
    var prevBtn = qs('[data-hc-prev]', root);
    var nextBtn = qs('[data-hc-next]', root);
    var autoplay = root.dataset.autoplay === 'true';
    var speed = (parseFloat(root.dataset.autoplaySpeed) || 6) * 1000;
    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var index = 0;
    var timer = null;

    function goTo(i) {
      index = (i + slides.length) % slides.length;
      track.style.transform = 'translateX(-' + (index * 100) + '%)';
      slides.forEach(function (s, si) { s.classList.toggle('is-active', si === index); });
      tabs.forEach(function (t, ti) {
        var active = ti === index;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', String(active));
        var fill = qs('.hc-tab-fill', t);
        if (!fill) return;
        fill.style.transition = 'none';
        fill.style.width = '0%';
        if (active && autoplay && !reduceMotion) {
          // restart the fill animation on the newly active tab
          requestAnimationFrame(function () {
            fill.style.transition = 'width ' + speed + 'ms linear';
            fill.style.width = '100%';
          });
        }
      });
    }

    function next() { goTo(index + 1); }
    function prev() { goTo(index - 1); }

    function stopAutoplay() {
      if (timer) { clearInterval(timer); timer = null; }
    }
    function startAutoplay() {
      if (!autoplay || reduceMotion) return;
      stopAutoplay();
      timer = setInterval(next, speed);
    }

    on(prevBtn, 'click', function () { prev(); startAutoplay(); });
    on(nextBtn, 'click', function () { next(); startAutoplay(); });
    tabs.forEach(function (t, i) { on(t, 'click', function () { goTo(i); startAutoplay(); }); });

    on(root, 'mouseenter', stopAutoplay);
    on(root, 'mouseleave', startAutoplay);
    on(root, 'focusin', stopAutoplay);
    on(root, 'focusout', startAutoplay);

    // Swipe support
    var startX = null;
    on(track, 'pointerdown', function (e) { startX = e.clientX; stopAutoplay(); });
    on(track, 'pointerup', function (e) {
      if (startX === null) return;
      var dx = e.clientX - startX;
      if (Math.abs(dx) > 50) { dx < 0 ? next() : prev(); }
      startX = null;
      startAutoplay();
    });

    on(document, 'keydown', function (e) {
      if (!root.contains(document.activeElement)) return;
      if (e.key === 'ArrowRight') { next(); startAutoplay(); }
      if (e.key === 'ArrowLeft') { prev(); startAutoplay(); }
    });

    goTo(0);
    startAutoplay();
  }

  /* ============================================================
     PRODUCT CAROUSEL (best sellers, etc.) — scroll-snap track,
     buttons just call scrollBy(); native swipe/snap handles touch.
  ============================================================ */
  function initProductCarousels() {
    qsa('[data-carousel-track]').forEach(function (track) {
      var nav = track.closest('.section').querySelector('[data-carousel-nav]');
      if (!nav) return;
      var prevBtn = qs('[data-carousel-prev]', nav);
      var nextBtn = qs('[data-carousel-next]', nav);

      function updateButtons() {
        var max = track.scrollWidth - track.clientWidth;
        prevBtn.disabled = track.scrollLeft <= 1;
        nextBtn.disabled = track.scrollLeft >= max - 1;
      }

      function page(dir) {
        track.scrollBy({ left: dir * track.clientWidth, behavior: 'smooth' });
      }

      on(prevBtn, 'click', function () { page(-1); });
      on(nextBtn, 'click', function () { page(1); });
      on(track, 'scroll', updateButtons, { passive: true });
      window.addEventListener('resize', updateButtons);

      updateButtons();
      // A collection fetched after this fires (e.g. via the theme editor) can
      // change scrollWidth without a scroll/resize event — recheck once images load.
      window.addEventListener('load', updateButtons);
    });
  }

  /* ============================================================
     GALLERY — thumbnail switching
  ============================================================ */
  function initGallery() {
    var gallery = qs('[data-gallery]');
    if (!gallery) return;
    var mainImg = qs('#main-product-image');
    var thumbs = qsa('[data-thumb]', gallery);

    // Thumbnail click — swap main image using the pre-built full-size URL
    thumbs.forEach(function (thumb) {
      on(thumb, 'click', function () {
        thumbs.forEach(function (t) { t.classList.remove('active'); });
        thumb.classList.add('active');
        if (mainImg && thumb.dataset.fullSrc) {
          // Must remove srcset so the browser doesn't ignore the src swap
          mainImg.removeAttribute('srcset');
          mainImg.removeAttribute('sizes');
          mainImg.src = thumb.dataset.fullSrc;
        }
      });
    });

    // Zoom button — open lightbox with current main image
    var zoomBtn = qs('.zoom', gallery);
    var lightbox = qs('#pdp-lightbox');
    var lightboxImg = qs('#pdp-lightbox-img');
    var lightboxClose = qs('#pdp-lightbox-close');

    function openLightbox() {
      if (!lightbox || !lightboxImg || !mainImg) return;
      lightboxImg.src = mainImg.src;
      lightboxImg.alt = mainImg.alt;
      lightbox.style.display = 'flex';
      document.body.style.overflow = 'hidden';
      lightboxClose.focus();
    }
    function closeLightbox() {
      if (!lightbox) return;
      lightbox.style.display = 'none';
      document.body.style.overflow = '';
      if (zoomBtn) zoomBtn.focus();
    }

    on(zoomBtn, 'click', openLightbox);
    on(lightboxClose, 'click', closeLightbox);
    on(lightbox, 'click', function (e) {
      if (e.target === lightbox) closeLightbox();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && lightbox && lightbox.style.display === 'flex') closeLightbox();
    });
  }

  /* ============================================================
     PDP ACCORDION (Description / Specifications / Reviews / FAQ)
  ============================================================ */
  function initPdpAccordion() {
    var root = qs('.pdp-accordion');
    if (!root) return;
    var items = qsa('.pdp-acc-item', root);

    function setOpen(item, open) {
      var head = qs('.pdp-acc-head', item);
      var body = qs('.pdp-acc-body', item);
      item.classList.toggle('is-open', open);
      head.setAttribute('aria-expanded', String(open));
      body.style.maxHeight = open ? body.scrollHeight + 'px' : '0';
    }

    // Keep an open item's max-height in sync with content that resizes
    // after the fact (e.g. the Judge.me reviews widget renders async).
    if (window.ResizeObserver) {
      items.forEach(function (item) {
        var body = qs('.pdp-acc-body', item);
        var ro = new ResizeObserver(function () {
          if (item.classList.contains('is-open')) {
            body.style.maxHeight = body.scrollHeight + 'px';
          }
        });
        ro.observe(body);
      });
    }

    on(root, 'click', function (e) {
      var head = e.target.closest('.pdp-acc-head');
      if (!head) return;
      var item = head.closest('.pdp-acc-item');
      var wasOpen = item.classList.contains('is-open');
      items.forEach(function (other) { if (other !== item) setOpen(other, false); });
      setOpen(item, !wasOpen);
    });

    // Open whichever item already carries .is-open (Description, by default).
    items.forEach(function (item) {
      if (item.classList.contains('is-open')) setOpen(item, true);
    });
  }

  /* ============================================================
     PDP DESCRIPTION READ MORE
  ============================================================ */
  function initDescReadMore() {
    var box = qs('[data-readmore]');
    if (!box) return;
    var content = qs('[data-readmore-content]', box);
    var btn = qs('[data-readmore-toggle]', box);
    if (!content || !btn) return;

    // Nothing to truncate — the text already fits the preview height.
    if (content.scrollHeight <= content.clientHeight + 4) {
      btn.style.display = 'none';
      return;
    }

    on(btn, 'click', function () {
      var expanded = box.classList.toggle('expanded');
      btn.textContent = expanded ? 'Read less' : 'Read more';
      var accBody = box.closest('.pdp-acc-body');
      if (accBody) accBody.style.maxHeight = accBody.scrollHeight + 'px';
    });
  }

  /* ============================================================
     FAQ ACCORDION
  ============================================================ */
  function initFAQ() {
    on(document, 'click', function (e) {
      var btn = e.target.closest('.faq-q');
      if (!btn) return;
      var item = btn.closest('.faq-item');
      var answer = qs('.faq-a', item);
      var isOpen = item.classList.contains('open');

      item.classList.toggle('open', !isOpen);
      btn.setAttribute('aria-expanded', String(!isOpen));
      if (answer) {
        answer.style.maxHeight = isOpen ? '0' : answer.scrollHeight + 300 + 'px';
      }
    });
  }

  /* ============================================================
     FILTER APPLY — shared by the checkbox and price-range handlers.
     Navigates immediately on desktop. On mobile, while the sidebar is
     relocated into the filter sheet, it stages the change instead:
     fetches an updated result count via the Section Rendering API
     (no navigation) and updates the "Show N results" button, which
     applies the staged URL when tapped.
  ============================================================ */
  var filterApply = { pendingUrl: null, applyBtn: null, sectionId: null };

  function isMobileFilterMode() {
    var sidebar = qs('.sidebar');
    return !!(sidebar && sidebar.classList.contains('mfs-content'));
  }

  function buildFilterUrl() {
    var url = new URL(window.location.href);
    var kept = new URLSearchParams();
    url.searchParams.forEach(function (v, k) {
      if (k.indexOf('filter.') !== 0) kept.append(k, v);
    });
    qsa('.filter-body input[type="checkbox"]:checked').forEach(function (c) {
      kept.append(c.name, c.value);
    });
    var minInput = qs('[data-range-input="min"]');
    var maxInput = qs('[data-range-input="max"]');
    if (minInput && minInput.value !== '') kept.set(minInput.name, minInput.value);
    if (maxInput && maxInput.value !== '') kept.set(maxInput.name, maxInput.value);
    var qsStr = kept.toString();
    return url.origin + url.pathname + (qsStr ? '?' + qsStr : '');
  }

  function stageFilterUrl(url) {
    filterApply.pendingUrl = url;
    var btn = filterApply.applyBtn;
    if (!btn || !filterApply.sectionId) return;
    btn.disabled = true;
    btn.textContent = 'Updating…';
    var sep = url.indexOf('?') > -1 ? '&' : '?';
    fetch(url + sep + 'section_id=' + filterApply.sectionId)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        if (filterApply.pendingUrl !== url) return; // a newer change superseded this one
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var meta = doc.querySelector('.results-meta');
        var count = meta ? parseInt(meta.textContent, 10) : NaN;
        btn.disabled = false;
        btn.textContent = isNaN(count) ? 'Show results' : 'Show ' + count + ' result' + (count === 1 ? '' : 's');
      })
      .catch(function () {
        if (filterApply.pendingUrl !== url) return;
        btn.disabled = false;
        btn.textContent = 'Show results';
      });
  }

  function applyOrStage(url) {
    url = url || buildFilterUrl();
    if (isMobileFilterMode()) {
      stageFilterUrl(url);
    } else {
      window.location.href = url;
    }
  }

  /* ============================================================
     FILTER ACCORDION
  ============================================================ */
  function initFilterAccordions() {
    qsa('[data-filter-group]').forEach(function (group) {
      var head = qs('.filter-head', group);
      var body = qs('.filter-body', group);
      if (!head || !body) return;

      on(head, 'click', function () {
        var collapsed = group.classList.toggle('collapsed');
        head.setAttribute('aria-expanded', String(!collapsed));
      });

      // Show more / show less for list filters with > limit items
      var limit = parseInt(body.dataset.filterLimit);
      if (!limit) return;
      var items = qsa('.f-check', body);
      if (items.length <= limit) return;

      // Hide items beyond the limit initially
      items.forEach(function (item, i) {
        if (i >= limit) item.style.display = 'none';
      });

      // Inject toggle button
      var remaining = items.length - limit;
      var moreBtn = document.createElement('button');
      moreBtn.className = 'filter-more-btn';
      moreBtn.setAttribute('aria-expanded', 'false');
      moreBtn.innerHTML =
        '<span>' + remaining + ' more</span>' +
        '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
          '<path d="M6 9l6 6 6-6"/>' +
        '</svg>';
      body.appendChild(moreBtn);

      on(moreBtn, 'click', function () {
        var open = moreBtn.classList.toggle('is-open');
        moreBtn.setAttribute('aria-expanded', String(open));
        items.forEach(function (item, i) {
          if (i >= limit) item.style.display = open ? '' : 'none';
        });
        moreBtn.querySelector('span').textContent = open ? 'Show less' : remaining + ' more';
      });
    });

    // Sort select — update URL
    var sortSelect = qs('#sort-select');
    if (sortSelect) {
      on(sortSelect, 'change', function () {
        var url = new URL(window.location.href);
        url.searchParams.set('sort_by', sortSelect.value);
        window.location.href = url.toString();
      });
    }

    // Filter checkboxes — apply immediately on desktop, stage on mobile
    qsa('.filter-body input[type="checkbox"]').forEach(function (cb) {
      on(cb, 'change', function () { applyOrStage(); });
    });
  }

  /* ============================================================
     COLLECTION SEARCH — client-side filter of the current page's grid
  ============================================================ */
  function initCollectionSearch() {
    var input = qs('#collection-search');
    var grid = qs('#product-grid');
    if (!input || !grid) return;
    var cards = qsa('.prod', grid);
    var emptyMsg = qs('#search-empty-msg');

    on(input, 'input', function () {
      var q = input.value.trim().toLowerCase();
      var visible = 0;
      cards.forEach(function (card) {
        var nameEl = qs('.prod-name', card);
        var name = nameEl ? nameEl.textContent.toLowerCase() : '';
        var match = q === '' || name.indexOf(q) > -1;
        card.style.display = match ? '' : 'none';
        if (match) visible++;
      });
      if (emptyMsg) emptyMsg.hidden = !(q !== '' && visible === 0);
    });
  }

  /* ============================================================
     PRICE RANGE SLIDER
  ============================================================ */
  function initPriceRangeSlider() {
    qsa('[data-price-filter]').forEach(function (wrap) {
      var track = qs('.f-range-track', wrap);
      var fill = qs('.fill', track);
      var h1 = qs('.handle.h1', track);
      var h2 = qs('.handle.h2', track);
      var minInput = qs('[data-range-input="min"]', wrap);
      var maxInput = qs('[data-range-input="max"]', wrap);
      if (!track || !fill || !h1 || !h2 || !minInput || !maxInput) return;

      var rangeMin = parseFloat(wrap.dataset.rangeMin) || 0;
      var rangeMax = parseFloat(wrap.dataset.rangeMax) || 100;
      if (rangeMax <= rangeMin) return;
      var step = Math.max(1, Math.round((rangeMax - rangeMin) / 100));

      var curMin = Math.max(rangeMin, Math.min(parseFloat(minInput.value), rangeMax)) || rangeMin;
      var curMax = Math.max(rangeMin, Math.min(parseFloat(maxInput.value), rangeMax)) || rangeMax;

      function valToPct(v) { return Math.min(100, Math.max(0, ((v - rangeMin) / (rangeMax - rangeMin)) * 100)); }
      function pctToVal(pct) { return Math.round(rangeMin + (pct / 100) * (rangeMax - rangeMin)); }

      function render() {
        var p1 = valToPct(curMin), p2 = valToPct(curMax);
        h1.style.left = p1 + '%';
        h2.style.left = p2 + '%';
        fill.style.left = p1 + '%';
        fill.style.right = (100 - p2) + '%';
        h1.setAttribute('aria-valuenow', curMin);
        h2.setAttribute('aria-valuenow', curMax);
      }
      render();

      function submit() { applyOrStage(); }

      function moveTo(clientX, isMin) {
        var rect = track.getBoundingClientRect();
        var pct = ((clientX - rect.left) / rect.width) * 100;
        var val = pctToVal(Math.min(100, Math.max(0, pct)));
        if (isMin) {
          curMin = Math.min(val, curMax);
          minInput.value = curMin;
        } else {
          curMax = Math.max(val, curMin);
          maxInput.value = curMax;
        }
        render();
      }

      function startDrag(isMin) {
        function onMouseMove(e) { moveTo(e.clientX, isMin); }
        function onTouchMove(e) { if (e.touches[0]) moveTo(e.touches[0].clientX, isMin); }
        function stop() {
          document.removeEventListener('mousemove', onMouseMove);
          document.removeEventListener('mouseup', stop);
          document.removeEventListener('touchmove', onTouchMove);
          document.removeEventListener('touchend', stop);
          submit();
        }
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', stop);
        document.addEventListener('touchmove', onTouchMove, { passive: true });
        document.addEventListener('touchend', stop);
      }

      on(h1, 'mousedown', function (e) { e.preventDefault(); startDrag(true); });
      on(h1, 'touchstart', function () { startDrag(true); }, { passive: true });
      on(h2, 'mousedown', function (e) { e.preventDefault(); startDrag(false); });
      on(h2, 'touchstart', function () { startDrag(false); }, { passive: true });

      function onKey(e, isMin) {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          if (isMin) { curMin = Math.max(rangeMin, curMin - step); minInput.value = curMin; }
          else { curMax = Math.max(curMin, curMax - step); maxInput.value = curMax; }
          render();
          e.preventDefault();
        } else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          if (isMin) { curMin = Math.min(curMax, curMin + step); minInput.value = curMin; }
          else { curMax = Math.min(rangeMax, curMax + step); maxInput.value = curMax; }
          render();
          e.preventDefault();
        } else if (e.key === 'Enter') {
          submit();
        }
      }
      on(h1, 'keydown', function (e) { onKey(e, true); });
      on(h2, 'keydown', function (e) { onKey(e, false); });

      on(minInput, 'change', function () {
        curMin = Math.min(Math.max(rangeMin, parseFloat(minInput.value) || rangeMin), curMax);
        minInput.value = curMin;
        render();
        submit();
      });
      on(maxInput, 'change', function () {
        curMax = Math.max(Math.min(rangeMax, parseFloat(maxInput.value) || rangeMax), curMin);
        maxInput.value = curMax;
        render();
        submit();
      });
    });
  }

  /* ============================================================
     MOBILE FILTER SHEET (Gymshark-style sticky bar + bottom sheet)
  ============================================================ */
  function initMobileFilterSheet() {
    var bar = qs('[data-mfb]');
    var sheet = qs('[data-mfs]');
    var sidebar = qs('.sidebar');
    var body = qs('[data-mfs-body]', sheet || document);
    if (!bar || !sheet || !sidebar || !body) return;

    var openBtn = qs('[data-mfb-filter-open]', bar);
    var closeEls = qsa('[data-mfb-filter-close]');
    var applyBtn = qs('[data-mfb-filter-apply]', sheet);
    var sortBtn = qs('[data-mfb-sort]', bar);
    var sortSelect = qs('#sort-select');

    filterApply.applyBtn = applyBtn;
    var sectionWrap = qs('#product-grid') && qs('#product-grid').closest('[id^="shopify-section-"]');
    filterApply.sectionId = sectionWrap ? sectionWrap.id.replace('shopify-section-', '') : null;

    // Relocate the sidebar into the sheet on mobile, and back on desktop —
    // it keeps its existing event listeners since this moves the same node.
    var sidebarHome = sidebar.parentNode;
    var sidebarNext = sidebar.nextSibling;
    var mq = window.matchMedia('(max-width: 880px)');
    function placeSidebar(isMobile) {
      if (isMobile && sidebar.parentNode !== body) {
        sidebar.classList.add('mfs-content');
        body.appendChild(sidebar);
      } else if (!isMobile && sidebar.parentNode === body) {
        sidebar.classList.remove('mfs-content');
        sidebarHome.insertBefore(sidebar, sidebarNext);
      }
    }
    placeSidebar(mq.matches);
    if (mq.addEventListener) {
      mq.addEventListener('change', function (e) { placeSidebar(e.matches); });
    }

    function openSheet() {
      sheet.classList.add('is-open');
      sheet.setAttribute('aria-hidden', 'false');
      openBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
    }
    function closeSheet() {
      sheet.classList.remove('is-open');
      sheet.setAttribute('aria-hidden', 'true');
      openBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
    }

    on(openBtn, 'click', openSheet);
    closeEls.forEach(function (el) { on(el, 'click', closeSheet); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && sheet.classList.contains('is-open')) closeSheet();
    });

    // "Show N results" — apply the staged filters if anything changed,
    // otherwise there's nothing to navigate to, so just close.
    if (applyBtn) {
      on(applyBtn, 'click', function () {
        if (filterApply.pendingUrl) {
          window.location.href = filterApply.pendingUrl;
        } else {
          closeSheet();
        }
      });
    }

    // "Sort" opens the existing native select — no separate sheet needed.
    if (sortBtn && sortSelect) {
      on(sortBtn, 'click', function () {
        if (typeof sortSelect.showPicker === 'function') {
          try { sortSelect.showPicker(); return; } catch (err) { /* fall through */ }
        }
        sortSelect.focus();
      });
    }
  }

  /* ============================================================
     DELIVERY ACCORDION
  ============================================================ */
  function initDeliveryAccordion() {
    var head = qs('[data-delivery-accordion] .delivery-head');
    var body = qs('#delivery-body');
    if (!head || !body) return;

    var toggle = qs('.toggle', head);
    on(head, 'click', function () {
      var isOpen = head.getAttribute('aria-expanded') === 'true';
      head.setAttribute('aria-expanded', String(!isOpen));
      body.style.display = isOpen ? 'none' : '';
      if (toggle) toggle.textContent = isOpen ? 'Show' : 'Hide';
    });
    on(head, 'keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); head.click(); }
    });
  }

  /* ============================================================
     COMPARE BAR
  ============================================================ */
  var compareState = {};

  function initCompareBar() {
    var bar = qs('#compare-bar');
    if (!bar) return;

    on(document, 'change', function (e) {
      var cb = e.target.closest('.compare-check');
      if (!cb) return;
      var id = cb.dataset.productId;

      if (cb.checked) {
        if (Object.keys(compareState).length >= 4) {
          cb.checked = false;
          return;
        }
        compareState[id] = {
          title: cb.dataset.productTitle,
          img: cb.dataset.productImg,
          handle: cb.dataset.productHandle || ''
        };
      } else {
        delete compareState[id];
      }
      renderCompareBar();
    });

    on(qs('#compare-clear'), 'click', function () {
      compareState = {};
      qsa('.compare-check:checked').forEach(function (cb) { cb.checked = false; });
      renderCompareBar();
    });

    on(qs('#compare-now'), 'click', function () {
      var handles = Object.values(compareState)
        .map(function (item) { return item.handle; })
        .filter(Boolean);
      if (handles.length) {
        window.location.href = '/pages/compare?handles=' + handles.join(',');
      }
    });

    on(bar, 'click', function (e) {
      var x = e.target.closest('.x');
      if (!x) return;
      var slot = x.closest('.compare-slot');
      if (!slot) return;
      var id = slot.dataset.productId;
      if (id) {
        delete compareState[id];
        var cb = qs('[data-product-id="' + id + '"].compare-check');
        if (cb) cb.checked = false;
      }
      renderCompareBar();
    });
  }

  function renderCompareBar() {
    var bar = qs('#compare-bar');
    if (!bar) return;
    var ids = Object.keys(compareState);
    bar.classList.toggle('show', ids.length > 0);
    bar.setAttribute('aria-hidden', String(ids.length === 0));

    var slots = qsa('.compare-slot', bar);
    slots.forEach(function (slot, i) {
      var id = ids[i];
      if (id) {
        var item = compareState[id];
        slot.classList.remove('empty');
        slot.dataset.productId = id;
        slot.innerHTML =
          '<img src="' + item.img + '" alt="' + item.title + '" width="60" height="60">' +
          '<span class="x" aria-label="Remove ' + item.title + '">×</span>';
      } else {
        slot.classList.add('empty');
        slot.removeAttribute('data-product-id');
        slot.innerHTML = '';
      }
    });
    shiftFAB();
  }

  /* ============================================================
     FAB SHIFT
  ============================================================ */
  function shiftFAB() {
    var fab = qs('#whatsapp-fab');
    if (!fab) return;
    var compareVisible = qs('#compare-bar.show') !== null;
    var stickyVisible = qs('#sticky-atc.show') !== null;
    var cartOpen = qs('#cart-drawer.is-open') !== null;
    fab.classList.toggle('shifted', compareVisible || stickyVisible || cartOpen);
  }

  /* ============================================================
     REVEAL ON SCROLL
  ============================================================ */
  function initReveal() {
    var elements = qsa('.reveal');
    if (!elements.length) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('in');
          io.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1 });
    elements.forEach(function (el) { io.observe(el); });
  }

  /* ============================================================
     VIEW TOGGLE (grid / list)
  ============================================================ */
  function initViewToggle() {
    var toggleBtns = qsa('[data-view]');
    var grid = qs('#product-grid');
    if (!toggleBtns.length || !grid) return;

    toggleBtns.forEach(function (btn) {
      on(btn, 'click', function () {
        toggleBtns.forEach(function (b) {
          b.classList.remove('active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
        grid.dataset.view = btn.dataset.view;
      });
    });
  }

  /* ============================================================
     QUICK-ADD (product card)
  ============================================================ */
  function initQuickAdd() {
    on(document, 'click', function (e) {
      var btn = e.target.closest('[data-atc]');
      if (!btn) return;
      var variantId = btn.dataset.atc;
      if (!variantId) return;
      // The button lives inside the product-image link (for layout), so
      // stop it from triggering that link's navigation to the product page.
      e.preventDefault();
      e.stopPropagation();

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      })
        .then(function (r) { return r.json(); })
        .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
        .then(function (cart) {
          renderCartDrawer(cart);
          var drawer = qs('#cart-drawer');
          var overlay = qs('.cart-drawer-overlay');
          if (drawer) {
            drawer.classList.add('is-open');
            overlay && overlay.classList.add('is-open');
            drawer.setAttribute('aria-hidden', 'false');
            document.body.style.overflow = 'hidden';
            shiftFAB();
          }
        })
        .catch(function (err) { console.error('Quick add error', err); });
    });
  }

  /* ============================================================
     INIT
  ============================================================ */
  document.addEventListener('DOMContentLoaded', function () {
    initHeader();
    initSearch();
    initMobileMenu();
    initCartDrawer();
    initQtyStepper();
    initVariants();
    initStickyATC();
    initHeroCarousel();
    initProductCarousels();
    initGallery();
    initPdpAccordion();
    initDescReadMore();
    initFAQ();
    initFilterAccordions();
    initCollectionSearch();
    initPriceRangeSlider();
    initMobileFilterSheet();
    initDeliveryAccordion();
    initCompareBar();
    initReveal();
    initViewToggle();
    initQuickAdd();
    initCopyLink();
    initWishlist();
    initMegaMenu();
    initQuickView();
    initSaveForLater();
  });

  /* ============================================================
     QUICK VIEW MODAL
  ============================================================ */
  function initQuickView() {
    var modal   = qs('#quick-view-modal');
    var overlay = qs('#quick-view-overlay');
    var body    = qs('#quick-view-body');
    var closeBtn = qs('#qv-close');
    if (!modal) return;

    var currentVariantId = null;

    function openQV(handle) {
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      body.innerHTML = '<div class="qv-loading"><span>Loading&hellip;</span></div>';

      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (p) { renderQV(p); })
        .catch(function () {
          body.innerHTML = '<p style="padding:24px;font-size:14px;color:var(--c-graphite);">Unable to load product.</p>';
        });
    }

    function closeQV() {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    function money(cents) {
      return 'KES ' + (cents / 100).toLocaleString('en-KE');
    }

    function renderQV(p) {
      var v = p.variants[0];
      currentVariantId = v.id;
      var imgSrc = p.images && p.images[0] ? p.images[0] : '';
      var comparePrice = v.compare_at_price > v.price
        ? '<span class="qv-price-old">' + money(v.compare_at_price) + '</span>' : '';

      var variantSelects = '';
      if (p.variants.length > 1) {
        variantSelects = '<div class="qv-variants">' +
          p.options.map(function (opt, oi) {
            return '<div class="qv-var-group">' +
              '<span class="qv-var-label">' + opt.name + '</span>' +
              '<div class="qv-var-options">' +
              opt.values.map(function (val, vi) {
                var isFirst = vi === 0;
                return '<button class="var-chip' + (isFirst ? ' active' : '') + '" data-qv-opt-index="' + oi + '" data-qv-opt-value="' + val + '">' + val + '</button>';
              }).join('') +
              '</div></div>';
          }).join('') +
          '</div>';
      }

      body.innerHTML =
        '<div class="qv-grid">' +
          '<div class="qv-image">' +
            (imgSrc ? '<img src="' + imgSrc + '" id="qv-main-img" alt="' + p.title + '" loading="eager">' : '') +
            (p.images && p.images.length > 1
              ? '<div class="qv-thumbs">' +
                  p.images.slice(0, 5).map(function (src, i) {
                    return '<div class="qv-thumb' + (i === 0 ? ' active' : '') + '" data-qv-img="' + src + '"><img src="' + src + '" alt="" loading="lazy"></div>';
                  }).join('') +
                '</div>'
              : '') +
          '</div>' +
          '<div class="qv-info">' +
            '<div class="qv-type">' + (p.product_type || '') + '</div>' +
            '<h2 class="qv-title">' + p.title + '</h2>' +
            '<div class="qv-price-row">' +
              '<span class="qv-price-now" id="qv-price">' + money(v.price) + '</span>' +
              comparePrice +
            '</div>' +
            variantSelects +
            '<div class="qv-actions">' +
              '<button class="btn btn--full" id="qv-atc-btn">' +
                'Add to cart +' +
              '</button>' +
              '<a href="/products/' + p.handle + '" class="btn btn--ghost btn--full">Full details &rarr;</a>' +
            '</div>' +
          '</div>' +
        '</div>';

      var variants = p.variants;

      // Thumbnail switching
      qsa('.qv-thumb', body).forEach(function (thumb) {
        on(thumb, 'click', function () {
          qsa('.qv-thumb', body).forEach(function (t) { t.classList.remove('active'); });
          thumb.classList.add('active');
          var mainImg = qs('#qv-main-img');
          if (mainImg) mainImg.src = thumb.dataset.qvImg;
        });
      });

      // Variant selection
      on(body, 'click', function (e) {
        var btn = e.target.closest('[data-qv-opt-value]');
        if (!btn) return;
        var optIdx = parseInt(btn.dataset.qvOptIndex);
        var optVal = btn.dataset.qvOptValue;

        qsa('[data-qv-opt-index="' + optIdx + '"]', body).forEach(function (b) { b.classList.remove('active'); });
        btn.classList.add('active');

        // Find matching variant
        var selected = {};
        qsa('[data-qv-opt-index]', body).forEach(function (b) {
          if (b.classList.contains('active')) selected[b.dataset.qvOptIndex] = b.dataset.qvOptValue;
        });
        var match = variants.find(function (v2) {
          return v2.options.every(function (o, i) { return !selected[i] || selected[i] === o; });
        }) || variants[0];
        currentVariantId = match.id;
        var priceEl = qs('#qv-price');
        if (priceEl) priceEl.textContent = money(match.price);
      });

      // ATC
      on(qs('#qv-atc-btn', body), 'click', function () {
        if (!currentVariantId) return;
        var atcBtn = qs('#qv-atc-btn', body);
        atcBtn.disabled = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: currentVariantId, quantity: 1 })
        })
          .then(function (r) { return r.json(); })
          .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
          .then(function (cart) {
            renderCartDrawer(cart);
            closeQV();
            var drawer = qs('#cart-drawer');
            var cartOverlay = qs('.cart-drawer-overlay');
            if (drawer) {
              drawer.classList.add('is-open');
              cartOverlay && cartOverlay.classList.add('is-open');
              drawer.setAttribute('aria-hidden', 'false');
              document.body.style.overflow = 'hidden';
              shiftFAB();
            }
          })
          .finally(function () { if (atcBtn) atcBtn.disabled = false; });
      });
    }

    on(document, 'click', function (e) {
      var btn = e.target.closest('[data-quickview]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      openQV(btn.dataset.quickview);
    });
    on(closeBtn, 'click', closeQV);
    on(overlay, 'click', closeQV);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal.classList.contains('is-open')) closeQV();
    });
  }

  /* ============================================================
     SAVE FOR LATER
  ============================================================ */
  function initSaveForLater() {
    var KEY = 'dfh_saved';

    function getSaved() {
      try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch(e) { return []; }
    }
    function setSaved(list) { localStorage.setItem(KEY, JSON.stringify(list)); }

    function renderSFLSection() {
      var saved = getSaved();
      var wrap  = qs('#saved-for-later');
      var list  = qs('#sfl-list');
      var count = qs('#sfl-count');
      if (!wrap || !list) return;

      if (!saved.length) { wrap.style.display = 'none'; return; }
      wrap.style.display = 'block';
      if (count) count.textContent = '(' + saved.length + ')';

      list.innerHTML = saved.map(function (item) {
        return '<div class="sfl-item" data-sfl-variant="' + item.variantId + '">' +
          (item.img ? '<img src="' + item.img + '" alt="' + item.title + '" width="48" height="48" loading="lazy">' : '') +
          '<div class="sfl-info">' +
            '<div class="sfl-name">' + item.title + '</div>' +
            '<div class="sfl-price">KES ' + (item.price / 100).toLocaleString('en-KE') + '</div>' +
          '</div>' +
          '<button class="sfl-move" data-sfl-move="' + item.variantId + '" aria-label="Move to cart">Move to cart</button>' +
          '<button class="sfl-remove" data-sfl-remove="' + item.variantId + '" aria-label="Remove saved item">' +
            '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="12" height="12" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12"/></svg>' +
          '</button>' +
        '</div>';
      }).join('');
    }

    // Save for later click
    on(qs('[data-cart-body]'), 'click', function (e) {
      var btn = e.target.closest('[data-save-later]');
      if (!btn) return;
      var key  = btn.dataset.saveLater;
      var item = {
        variantId: btn.dataset.saveVariant,
        handle:    btn.dataset.saveHandle,
        title:     btn.dataset.saveTitle,
        price:     parseInt(btn.dataset.savePrice),
        img:       btn.dataset.saveImg
      };
      var saved = getSaved();
      if (!saved.find(function (s) { return s.variantId === item.variantId; })) {
        saved.push(item);
        setSaved(saved);
      }
      // Remove from cart
      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity: 0 })
      })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          renderCartDrawer(cart);
          renderSFLSection();
        });
    });

    // Move to cart / remove from saved
    on(document, 'click', function (e) {
      var moveBtn   = e.target.closest('[data-sfl-move]');
      var removeBtn = e.target.closest('[data-sfl-remove]');

      if (moveBtn) {
        var vid = moveBtn.dataset.sflMove;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: vid, quantity: 1 })
        })
          .then(function (r) { return r.json(); })
          .then(function () { return fetch('/cart.js').then(function (r) { return r.json(); }); })
          .then(function (cart) {
            var saved = getSaved().filter(function (s) { return s.variantId !== vid; });
            setSaved(saved);
            renderCartDrawer(cart);
            renderSFLSection();
          });
        return;
      }

      if (removeBtn) {
        var vid2 = removeBtn.dataset.sflRemove;
        var saved2 = getSaved().filter(function (s) { return s.variantId !== vid2; });
        setSaved(saved2);
        renderSFLSection();
      }
    });

    // Cart note debounce
    var noteTimer;
    on(document, 'input', function (e) {
      if (!e.target.matches('#cart-note')) return;
      clearTimeout(noteTimer);
      noteTimer = setTimeout(function () {
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: e.target.value })
        });
      }, 600);
    });

    renderSFLSection();
  }

  /* ============================================================
     WISHLIST — localStorage-based heart toggle
  ============================================================ */
  function initWishlist() {
    var KEY = 'dfh_wishlist';
    var HANDLE_KEY = 'dfh_wishlist_handles';

    function getIds() {
      try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch(e) { return []; }
    }
    function getHandles() {
      try { return JSON.parse(localStorage.getItem(HANDLE_KEY)) || []; } catch(e) { return []; }
    }
    function save(ids, handles) {
      localStorage.setItem(KEY, JSON.stringify(ids));
      localStorage.setItem(HANDLE_KEY, JSON.stringify(handles));
    }
    function isWishlisted(id) { return getIds().indexOf(String(id)) > -1; }

    function syncButtons() {
      qsa('[data-wishlist]').forEach(function (btn) {
        var id = String(btn.dataset.wishlist);
        var active = isWishlisted(id);
        btn.classList.toggle('is-wishlisted', active);
        btn.setAttribute('aria-pressed', String(active));
        var svg = btn.querySelector('svg');
        if (svg) {
          svg.style.fill = active ? 'var(--c-red)' : 'none';
          svg.style.stroke = active ? 'var(--c-red)' : 'currentColor';
        }
      });
    }

    on(document, 'click', function (e) {
      var btn = e.target.closest('[data-wishlist]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      var id = String(btn.dataset.wishlist);
      var handle = btn.dataset.wishlistHandle || btn.dataset.wishlist;
      var ids = getIds();
      var handles = getHandles();
      var idx = ids.indexOf(id);
      if (idx > -1) {
        ids.splice(idx, 1);
        var hi = handles.indexOf(String(handle));
        if (hi > -1) handles.splice(hi, 1);
      } else {
        ids.push(id);
        if (handle && handles.indexOf(String(handle)) === -1) handles.push(String(handle));
      }
      save(ids, handles);
      syncButtons();
    });

    syncButtons();
  }

  /* ============================================================
     MEGA MENU — hover intent dropdown
  ============================================================ */
  function initMegaMenu() {
    var triggers = qsa('[data-mega-trigger]');
    if (!triggers.length) return;
    var activeTimer = null;
    var activeTrigger = null;

    function openPanel(triggerEl) {
      var key = triggerEl.dataset.megaTrigger;
      var panel = qs('[data-mega-panel="' + key + '"]');
      if (!panel) return;
      // Close any open panel first
      closeAll();
      triggerEl.classList.add('mega-active');
      triggerEl.setAttribute('aria-expanded', 'true');
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      activeTrigger = triggerEl;
    }

    function closeAll() {
      qsa('[data-mega-trigger]').forEach(function (t) {
        t.classList.remove('mega-active');
        t.setAttribute('aria-expanded', 'false');
      });
      qsa('[data-mega-panel]').forEach(function (p) {
        p.classList.remove('is-open');
        p.setAttribute('aria-hidden', 'true');
      });
      activeTrigger = null;
    }

    triggers.forEach(function (trigger) {
      on(trigger, 'mouseenter', function () {
        clearTimeout(activeTimer);
        activeTimer = setTimeout(function () { openPanel(trigger); }, 120);
      });
      on(trigger, 'mouseleave', function () {
        clearTimeout(activeTimer);
        activeTimer = setTimeout(closeAll, 200);
      });
      on(trigger, 'focus', function () { openPanel(trigger); });
    });

    // Keep open when hovering panel
    qsa('[data-mega-panel]').forEach(function (panel) {
      on(panel, 'mouseenter', function () { clearTimeout(activeTimer); });
      on(panel, 'mouseleave', function () {
        activeTimer = setTimeout(closeAll, 200);
      });
    });

    // Close on outside click / Escape
    document.addEventListener('click', function (e) {
      if (!e.target.closest('[data-mega-trigger],[data-mega-panel]')) closeAll();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeAll();
    });
  }

  /* ============================================================
     COPY LINK — article share bar
  ============================================================ */
  function initCopyLink() {
    on(document, 'click', function (e) {
      var btn = e.target.closest('[data-copy-url]');
      if (!btn) return;
      navigator.clipboard.writeText(btn.dataset.copyUrl).then(function () {
        var span = btn.querySelector('span') || btn;
        var orig = span.textContent;
        span.textContent = 'Copied!';
        setTimeout(function () { span.textContent = orig; }, 2000);
      });
    });
  }

})();
